import React, { useEffect, useState, useContext } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIGrid from "@mui/material/Grid2";
import MUITypography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListIcon from "@mui/material/ListItemIcon";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUIAddIcon from "@mui/icons-material/Add";
import MUIWheelchairPickupIcon from "@mui/icons-material/WheelchairPickup";
import MUIAlert from "@mui/material/Alert";

import Menu from "../../Menu";
import EmptyState from "../../EmptyState";
import DialogConfirm from "../../Dialog/DialogConfirm";
import Card from "../../Card";
import User from "../../../Utility/Crud/User";
import Security from "../../../Utility/Security";
import UploadedFile from "../../../Utility/Crud/UploadedFile";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import File from "../../../Utility/File";
import Format from "../../../Utility/Format";

const title = "Assessment";
const icon = MUIWheelchairPickupIcon;

const className = "CardChartAssessment";

export default function CardChartAssessment(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const [editMode, setEditMode] = useState(props.startInEditMode ?? false);
  const [cardChart, setCardChart] = useState(null);
  const [pendingAssessmentSave, setPendingAssessmentSave] = useState(false);

  const [mostRecentATPReport, setMostRecentATPReport] = useState(null);
  const order = chartContext.chart.orderActivity[chartContext.orderId];

  const atpAssessment =
    Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
      return (
        atpAssessment.orderActivityId === chartContext.orderId &&
        atpAssessment.deleted !== true
      );
    }) || [];
  const measurementsInSitting =
    Object.values(chartContext.chart.measurementInSitting).find(
      (measurement) => {
        return measurement.orderActivityId === chartContext.orderId;
      },
    ) || [];

  const deliveryInformation =
    Object.values(chartContext.chart?.deliveryInformation || {})
      .sort((a, b) => b.id - a.id)
      .find(
        (d) =>
          d &&
          d.id > 0 &&
          d.orderId === chartContext.orderId &&
          (d.documentType === "Home Assessment" ||
            d.documentType === "Home Assessment Web") &&
          d.documentInfoPruned &&
          !d.documentInfoPruned.includes("Home Assessment Completed") &&
          d.deleted === false,
      ) || {};

  const parsedHomeAssessmentDocument = deliveryInformation?.documentInfoPruned
    ? JSON.parse(deliveryInformation.documentInfoPruned)
    : {};
  const homeAssessmentDocument =
    Object.keys(parsedHomeAssessmentDocument).length === 0
      ? {}
      : parsedHomeAssessmentDocument;

  const atpAssessmentId = atpAssessment?.id ?? chartContext.getNegativeId();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const showSignatureAlert =
    props?.showSignatureAlert !== undefined ? props?.showSignatureAlert : true;

  const documentSignature =
    Object.values(chartContext.chart.documentSignature).find(
      (documentSignature) => {
        return (
          documentSignature.orderActivityId === chartContext.orderId &&
          atpAssessment.id === documentSignature.rowId
        );
      },
    ) || null;

  useEffect(() => {
    chartContext.resetChart();
    setCardChart(null);
  }, [chartContext.pageMode]);

  useEffect(() => {
    if (editMode && atpAssessment.id) {
      chartContext.updateValidationRules({
        key: className,
        rules: [
          {
            target: "atpAssessment",
            id: atpAssessment.id,
            field: "evaluationDate",
            rule: {
              required: true,
            },
          },
        ],
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, atpAssessment.id]);

  useEffect(() => {
    if (pendingAssessmentSave) {
      chartContext.handleSaveChart();
      setPendingAssessmentSave(false);
    }
  }, [pendingAssessmentSave]);

  useEffect(() => {
    if (!atpAssessment.id && editMode) {
      const atpAssessments = {
        [atpAssessmentId]: {
          id: atpAssessmentId,
          orderActivityId: chartContext.orderId,
          patientId: chartContext.patientId,
          measurementInSittingId:
            measurementsInSitting?.id || chartContext.newMeasurementId || null,
          evaluationDate: null,
          createdById: 1,
          deleted: false,
        },
      };
      chartContext.onChange({ atpAssessment: atpAssessments });
    }
  }, [atpAssessment.id, editMode]);

  useEffect(() => {
    setEditMode(props.startInEditMode ?? true);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Gets the uploaded files for the ATP assessment.
   */
  useEffect(() => {
    const uploadedFileIds = Object.values(chartContext.chart.activityFile)
      .filter((activityFile) => activityFile.activityId === order?.id)
      .map((activityFile) => activityFile.fileId);

    // Get the most recent ATP report file
    const mostRecentATPReport =
      uploadedFileIds
        .map((fileId) => {
          const uploadedFile = chartContext.chart.uploadedFile[fileId];
          if (!uploadedFile) {
            return null;
          }

          if (
            UploadedFile.getActivityFile(uploadedFile, chartContext.chart)
              ?.deleted === true
          ) {
            return null;
          }

          return uploadedFile;
        })
        .filter(Boolean)
        .filter((file) =>
          file.filename.toLowerCase().includes("assistive-technology-report"),
        )
        .sort((a, b) => {
          const dateA = a.uploadedAt ? new Date(a.uploadedAt) : new Date(0);
          const dateB = b.uploadedAt ? new Date(b.uploadedAt) : new Date(0);
          return dateB - dateA;
        })[0] || null;

    setMostRecentATPReport(mostRecentATPReport);
  }, [chartContext.chart.uploadedFile, chartContext.originalChart]);

  /**
   * Determines which assessment type the current user can access for the current order.
   * Complex orders require an ATP assessment, accessible only to the assigned ATP user or a user with administrator privileges.
   * Non-complex orders use the home assessment, accessible to any user.
   *
   * @returns {"atpAssessment"|"homeAssessment"|null}
   *   - Returns "atpAssessment" if the order is complex and the user is the assigned ATP or an administrator.
   *   - Returns "homeAssessment" if the order is not complex.
   *   - Returns null if the order is complex and the user is neither the assigned ATP nor an administrator.
   */

  const getAssessmentAccessType = () => {
    const isComplexOrder = OrderActivity.isComplex(
      order,
      chartContext.chart,
      mainContext.productTypesIndexed,
    );

    if (isComplexOrder) {
      const isAssignedAtpOrAdministrator =
        order?.atpId === mainContext.currentUser.id ||
        Security.hasRole(mainContext.currentUser, ["ADMIN"]);

      return isAssignedAtpOrAdministrator ? "atpAssessment" : null;
    }

    return "homeAssessment";
  };

  const renderHomeAssessmentReadOnlyView = () => {
    return (
      <MUIGrid container direction="row" spacing={2}>
        <MUIGrid size="auto">
          <MUITypography variant="subtitle2">Uploaded On</MUITypography>
          <MUITypography variant="body2">
            {deliveryInformation.uploadedOn
              ? Format.dateTime(deliveryInformation.uploadedOn)
              : "None"}
          </MUITypography>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view of measurements.
   * @returns {JSX.Element}
   */
  const renderATPAssessmentReadOnlyView = () => {
    const handlePdfViewer = (uploadedFile) => {
      let sanitizedFileName = File.sanitizeUrlFilename(uploadedFile.filename);
      window.open(`/file/${uploadedFile.id}/${sanitizedFileName}`, "_blank");
    };

    return (
      <>
        <MUIGrid container>
          <MUIGrid container direction="row" spacing={2}>
            <MUIGrid size="auto">
              <MUITypography variant="subtitle2">Evaluation Date</MUITypography>
              <MUITypography variant="body2">
                {atpAssessment && atpAssessment.evaluationDate
                  ? moment(atpAssessment.evaluationDate).format("M/D/YYYY")
                  : "None"}
              </MUITypography>
            </MUIGrid>
            <MUIGrid size="auto">
              <MUITypography variant="subtitle2">Created By</MUITypography>
              <MUITypography variant="body2">
                {User.getDisplayName(
                  mainContext.usersIndexed[atpAssessment["createdById"]],
                ) || "None"}
              </MUITypography>
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
        {atpAssessment.id && showSignatureAlert && (
          <MUIAlert
            sx={{ mt: 2 }}
            severity={documentSignature ? "success" : "warning"}
            action={
              !documentSignature &&
              getAssessmentAccessType() === "atpAssessment" ? (
                <MUIButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    chartContext.handleNavigateChart(
                      `/chart/${chartContext.patientId}/order/${chartContext.orderId}/atpAssessmentSign`,
                    );
                  }}
                >
                  Sign
                </MUIButton>
              ) : documentSignature ? (
                <MUIButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    if (mostRecentATPReport) {
                      handlePdfViewer(mostRecentATPReport);
                    }
                  }}
                >
                  View
                </MUIButton>
              ) : null
            }
          >
            {documentSignature
              ? "This ATP Assessment is signed."
              : "This ATP Assessment is not yet signed."}
          </MUIAlert>
        )}
      </>
    );
  };

  const renderConfirmDialog = () => {
    const isHomeAssessment = getAssessmentAccessType() === "homeAssessment";
    const confirmDialogHeader = isHomeAssessment
      ? "Delete Home Assessment"
      : "Delete ATP Assessment";
    const confirmDialogText = isHomeAssessment
      ? "Delete this home assessment? This action cannot be undone."
      : atpAssessment.activityFileId
        ? "Delete this ATP assessment and its linked attachment? This action cannot be undone."
        : "Delete this ATP assessment? This action cannot be undone.";

    return (
      <DialogConfirm
        header={confirmDialogHeader}
        text={confirmDialogText}
        open={openConfirmDialog}
        onConfirm={() => handleDeleteAssessment()}
        onClose={() => setOpenConfirmDialog(false)}
      />
    );
  };

  const renderContent = () => {
    let assessmentContent;
    if (
      getAssessmentAccessType() === "homeAssessment" &&
      Object.keys(homeAssessmentDocument).length > 0
    ) {
      assessmentContent = renderHomeAssessmentReadOnlyView(
        homeAssessmentDocument,
      );
    } else if (Object.values(atpAssessment).length > 0) {
      assessmentContent = renderATPAssessmentReadOnlyView();
    } else {
      assessmentContent = (
        <EmptyState line1="Nothing to show" line2="Better luck next time!" />
      );
    }

    return (
      <>
        {assessmentContent}
        {renderConfirmDialog()}
      </>
    );
  };

  /**
   * Deletes the assessment based on the current access type. For a home assessment, only the
   * home assessment record in delivery information is deleted. For an ATP assessment, the ATP
   * assessment, its linked activity file, its delivery information, and (outside normal edit
   * mode) the related measurement in sitting are all deleted.
   */
  const handleDeleteAssessment = () => {
    if (getAssessmentAccessType() === "homeAssessment") {
      chartContext.onChange({
        deliveryInformation: {
          [deliveryInformation.id]: {
            deleted: true,
          },
        },
      });
      setPendingAssessmentSave(true);
      setCardChart(null);
      setOpenConfirmDialog(false);
      return;
    }
    const updateData = {
      atpAssessment: {
        [atpAssessment.id]: {
          deleted: true,
        },
      },
    };

    if (deliveryInformation?.id) {
      updateData.deliveryInformation = {
        [deliveryInformation.id]: {
          deleted: true,
        },
      };
    }

    if (atpAssessment.activityFileId) {
      updateData.activityFile = {
        [atpAssessment.activityFileId]: {
          deleted: true,
        },
      };
    }

    if (!props.enableNormalEditMode && measurementsInSitting.id) {
      updateData.measurementInSitting = {
        [measurementsInSitting.id]: {
          deleted: true,
        },
      };
    }

    chartContext.onChange(updateData);
    setPendingAssessmentSave(true);
    setCardChart(null);
    setOpenConfirmDialog(false);
  };

  /**
   * Returns the action buttons for the card component. Depending on the current assessment
   * access type and what is already on file, this renders an Edit button (navigating to the
   * ATP or home assessment editor), a Create button (for a new ATP or home assessment), and a
   * Delete menu action.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const actions = [];

    const canManageAssessment =
      !chartContext.isUpdating &&
      Security.hasPermission("module.manageAtpAssessment");

    if (chartContext.pageMode !== "atpAssessment") {
      if (
        Object.values(atpAssessment).length > 0 &&
        !documentSignature &&
        getAssessmentAccessType() === "atpAssessment"
      ) {
        // Edit an existing, unsigned ATP assessment.
        actions.push(
          <MUIButton
            disabled={!canManageAssessment}
            onClick={() =>
              chartContext.handleNavigateChart(
                `/chart/${chartContext.patientId}/order/${chartContext.orderId}/atpAssessmentEdit`,
              )
            }
            size="small"
          >
            Edit
          </MUIButton>,
        );
      } else if (
        !Object.values(atpAssessment).length > 0 &&
        getAssessmentAccessType() === "homeAssessment" &&
        deliveryInformation?.id > 0
      ) {
        // Edit an existing home assessment on file.
        actions.push(
          <MUIButton
            disabled={chartContext.isUpdating}
            onClick={() =>
              chartContext.handleNavigateChart(
                `/chart/${chartContext.patientId}/order/${chartContext.orderId}/homeAssessmentEdit`,
              )
            }
            size="small"
          >
            Edit
          </MUIButton>,
        );
      } else if (
        !Object.values(atpAssessment).length > 0 &&
        getAssessmentAccessType()
      ) {
        // Create a new assessment of the type the current user is allowed to create.
        actions.push(
          <MUIButton
            variant={"outlined"}
            startIcon={<MUIAddIcon />}
            disabled={chartContext.isUpdating || chartContext.hasPendingChanges} // Prevents taking user to wrong page if complex equipment is added or removed and has not yet been saved. handleNavigateChart stores the path in state
            onClick={() =>
              chartContext.handleNavigateChart(
                `/chart/${chartContext.patientId}/order/${chartContext.orderId}/${getAssessmentAccessType()}`,
              )
            }
            size="small"
          >
            {`Create ${getAssessmentAccessType() === "atpAssessment" ? "ATP" : "Home"} Assessment`}
          </MUIButton>,
        );
      }
    }

    if (
      (atpAssessment.id > 0 && chartContext.pageMode !== "atpAssessment") ||
      (getAssessmentAccessType() === "homeAssessment" &&
        deliveryInformation?.id > 0)
    ) {
      actions.push(
        <Menu
          menuItems={[
            <MUIMenuItem
              disabled={!canManageAssessment}
              onClick={() => {
                if (chartContext.hasPendingChanges) {
                  chartContext.handleSaveChart(() => {
                    setOpenConfirmDialog(true);
                  });
                  return;
                }
                setOpenConfirmDialog(true);
              }}
            >
              <MUIListIcon>
                <MUIDeleteIcon />
              </MUIListIcon>
              Delete
            </MUIMenuItem>,
          ]}
        />,
      );
    }

    return actions;
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getContainerType={() => "inline-block"}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartAssessment.meta = { className: className, title: title, icon: icon };
